import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import rehypeSlug from 'rehype-slug';
import Markdown from '/@/components/markdown';
import type { HelpPageDetail, HelpVideo } from '/@/hooks/help-center';
import { _ } from '/@/locale';

/**
 * Insert `<br>` at Windows-style blank lines. A newline after `<br>` is required so markdown
 * headings/lists on the next line still parse (`# H1<br>## H2` would break; `<br>\n## H2` works).
 */
function replaceCrLfParagraphBreaksWithBr(source: string): string {
    return source.replace(/\r\n\r\n/g, '\n\n<br>\n\n');
}

function addTitleToMarkdown(markdown: string, title: string): string {
    return `# ${title}\n\n<br>\n\n${markdown}`;
}

/** Replace help-center placeholders with raw video tags (parsed via Markdown rehype-raw). */
function embedVideoPlaceholders(markdown: string, videos: HelpVideo[]): string {
    const byKey = new Map(videos.map((v) => [v.embedKey, v.fileUrl]));
    return markdown.replace(/\[\[video:([a-zA-Z0-9_-]+)\]\]/g, (_, key: string) => {
        const url = byKey.get(key);
        if (!url) {
            return '';
        }
        return `\n\n<video src="${url}" playsinline></video>\n\n`;
    });
}

function HelpArticleVideo({ src, title }: { src?: string | null; title?: string | null }) {
    const theme = useTheme();
    const [playing, setPlaying] = useState(false);

    if (!src) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                width: '100%',
                minHeight: { xs: 220, sm: 360 },
                bgcolor: 'grey.900',
            }}
        >
            {!playing ? (
                <>
                    <Box
                        component='video'
                        src={src}
                        muted
                        playsInline
                        preload='metadata'
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: 0.5,
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                                'linear-gradient(180deg, rgba(6,23,33,0.65) 0%, rgba(6,23,33,0.15) 40%, rgba(6,23,33,0) 100%)',
                        }}
                    />
                    <Stack
                        spacing={1}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            p: 2,
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            variant='h6'
                            sx={{
                                color: 'common.white',
                                fontWeight: 500,
                                fontSize: 20,
                                lineHeight: '24px',
                            }}
                        >
                            {title?.trim() ? title : _('Video')}
                        </Typography>
                    </Stack>
                    <IconButton
                        aria-label={_('Reproducir video')}
                        onClick={() => setPlaying(true)}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.18),
                            color: 'primary.main',
                            p: 1,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.28) },
                        }}
                    >
                        <PlayArrowIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                </>
            ) : (
                <Box
                    component='video'
                    src={src}
                    controls
                    autoPlay
                    playsInline
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        maxHeight: { xs: 360, sm: 480 },
                    }}
                />
            )}
        </Box>
    );
}

const HelpCenterMarkdownContent = ({ page }: { page: HelpPageDetail }) => {
    const { content, contentHtml, videos } = page;
    const md = _(content);
    const htmlFallback = _(contentHtml);
    const strippedMarkdown = useMemo(() => {
        const raw = md.trim() ? md : htmlFallback;
        const withBr = replaceCrLfParagraphBreaksWithBr(raw);
        const withTitle = addTitleToMarkdown(withBr, _(page.title));
        return embedVideoPlaceholders(withTitle, videos ?? []);
    }, [md, htmlFallback, videos, page.title]);

    const videoByUrl = useMemo(() => new Map((videos ?? []).map((v) => [v.fileUrl ?? '', v])), [videos]);

    const scrollSx = { scrollMarginTop: { xs: '88px', md: '100px' } };
    const articleMarkdownComponents = {
        h1: ({ children, id }: { children?: ReactNode; id?: string }) => (
            <Typography component='h1' id={id} variant='h5' color='primary' sx={scrollSx}>
                {children}
            </Typography>
        ),
        h2: ({ children, id }: { children?: ReactNode; id?: string }) => (
            <Typography component='h2' id={id} variant='h6' color='primary' sx={scrollSx}>
                {children}
            </Typography>
        ),
        h3: ({ children, id }: { children?: ReactNode; id?: string }) => (
            <Typography component='h3' id={id} variant='subtitle1' fontWeight='bold' color='primary' sx={scrollSx}>
                {children}
            </Typography>
        ),
        p: ({ children }: { children?: ReactNode }) => (
            <Typography variant='body2' color='primary'>
                {children}
            </Typography>
        ),
        strong: ({ children }: { children?: ReactNode }) => (
            <Typography variant='body2' color='primary' component='span' fontWeight='bold'>
                {children}
            </Typography>
        ),
        video: ({ src }: { src?: string }) => {
            const video = src ? videoByUrl.get(src) : undefined;
            return <HelpArticleVideo src={src} title={video?.title ? _(video.title) : null} />;
        },
    };

    if (!strippedMarkdown.trim()) {
        return <Typography color='text.secondary'>{_('Sin contenido.')}</Typography>;
    }

    return (
        <Markdown rehypePlugins={[rehypeSlug]} components={articleMarkdownComponents}>
            {strippedMarkdown}
        </Markdown>
    );
};

export default HelpCenterMarkdownContent;
